/**
 * Interests Modal Component
 * Reusable modal for selecting/editing user interests (tags)
 * Uses Alpine.js for reactivity and existing API endpoints
 */

import { lockBodyScroll, unlockBodyScroll } from '../modules/scroll-lock.js';
import { SM_BREAKPOINT } from '../modules/breakpoints.js';

export { interestsModal, interestsDisplay, searchInterestsFilter, TAG_CATEGORIES, getCategoryInfo };

// Helper to get translation (falls back to key if i18n not available)
function t(key) {
    return window.t ? window.t(key) : key;
}

// Tag category configuration with Lucide icons and consistent styling
// Names are fetched from i18n system at runtime
const TAG_CATEGORIES = {
    0: { nameKey: 'common.category_most_popular', icon: 'star', color: 'text-amber-400' },
    10: { nameKey: 'common.category_food_drink', icon: 'utensils', color: 'text-orange-500' },
    11: { nameKey: 'common.category_music', icon: 'music', color: 'text-violet-500' },
    12: { nameKey: 'common.category_movies_tv', icon: 'film', color: 'text-sky-500' },
    13: { nameKey: 'common.category_fashion_beauty', icon: 'sparkles', color: 'text-pink-400' },
    14: { nameKey: 'common.category_sports', icon: 'trophy', color: 'text-emerald-500' },
    15: { nameKey: 'common.category_travel', icon: 'plane', color: 'text-sky-500' },
    16: { nameKey: 'common.category_profession', icon: 'briefcase', color: 'text-slate-500' },
    17: { nameKey: 'common.category_games', icon: 'gamepad-2', color: 'text-indigo-500' },
    18: { nameKey: 'common.category_hobbies', icon: 'palette', color: 'text-rose-400' },
    19: { nameKey: 'common.category_books_culture', icon: 'book-open', color: 'text-amber-600' },
    20: { nameKey: 'common.category_other', icon: 'lightbulb', color: 'text-yellow-500' }
};

// Add getter for translated name
Object.keys(TAG_CATEGORIES).forEach(key => {
    const cat = TAG_CATEGORIES[key];
    Object.defineProperty(cat, 'name', {
        get: function() {
            return t(this.nameKey);
        },
        enumerable: true
    });
});

// Constants
const MAX_TAGS = 25;
const MAX_DISPLAYED_CHIPS_MOBILE = 4;
const MAX_DISPLAYED_CHIPS_DESKTOP = 8;
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

// Curated list of popular, universally-appealing interests across all categories
// These are hand-picked to represent common hobbies, activities, and interests
const POPULAR_TAG_IDS = [
    // Music (11) - Popular genres and artists
    '36',    // Dance music
    '94',    // Reggae
    '25',    // Blues
    '93',    // Rap
    '51',    // Indie
    '47',    // Funk
    '91',    // R&B
    '46',    // Folk music
    '105',   // Techno
    '31',    // Classic
    '114',   // Trance
    '1661',  // Guitar
    '2533',  // Concerts
    '67',    // Live Music
    // Food & Drink (10) - Universal foods and cuisines
    '1526',  // Coffee
    '1000002105', // Sushi
    '131122', // Pizza
    '2889',  // Cocktails
    '3149',  // Thai Food
    '3044',  // Mexican Food
    '2893',  // Indian Food
    '374983', // Cooking
    '8172',  // Green tea
    '1620',  // Tacos
    // Movies & TV (12) - Genres
    '170',   // Horror films
    '198',   // Romantic films
    '126',   // Animation
    '192',   // Quentin Tarantino
    // Sports (14) - Common activities
    '420',   // Gym
    '1000173265', // Hiking
    '15712', // Snow Boarding
    '5698',  // Water Sports
    '1000080398', // Working Out
    '15314', // Sports
    '121636', // Bodybuilding
    '14095', // Skateboard
    '342',   // Cricket
    '392',   // Rugby
    // Travel (15) - Popular destinations and activities
    '432',   // Backpacking
    '497',   // World Travel
    '438',   // Caribbean
    '512',   // Thailand
    '491',   // Paris
    '515',   // Tokyo
    '472',   // London
    '486',   // New York City
    '1789',  // Europe
    '428',   // Asia
    // Games (17)
    '630',   // Board games
    '637',   // Card games
    '623',   // Angry Birds
    '814',   // Video games
    // Hobbies (18) - Common activities
    '752',   // Drawing
    '53503', // Painting
    '1493',  // Singing
    '776',   // Languages
    '798',   // Scuba Diving
    '771',   // Ice skating
    '729',   // Baking
    '820',   // Wine Tasting
    '9419',  // YouTube
    '3045',  // Gadgets
    '785',   // Parks
    '2802',  // Cosplay
    // Books & Culture (19)
    '1886',  // Literature
    '53436', // Poetry
];

/**
 * Get category info for a tag
 * @param {number|string} catId - Category ID
 * @returns {Object} Category info with emoji, colors
 */
function getCategoryInfo(catId) {
    const id = parseInt(catId, 10);
    return TAG_CATEGORIES[id] || TAG_CATEGORIES[20];
}

/**
 * Alpine.js component factory for the interests modal
 */
function interestsModal() {
    return {
        // State
        isOpen: false,
        selectedTags: [],
        searchText: '',
        searchResults: [],
        activeCategory: null,
        isLoading: false,
        isSaving: false,
        hasMore: true,
        offset: 0,
        error: null,
        searchTimeout: null,
        onSaveCallback: null,
        callbackOnly: false,

        // Configuration
        maxTags: MAX_TAGS,
        maxDisplayedChips: window.innerWidth < SM_BREAKPOINT ? MAX_DISPLAYED_CHIPS_MOBILE : MAX_DISPLAYED_CHIPS_DESKTOP,
        categories: TAG_CATEGORIES,

        // Computed
        get selectedCount() {
            return this.selectedTags.length;
        },

        get canAddMore() {
            return this.selectedTags.length < this.maxTags;
        },

        get displayedSelectedTags() {
            return this.selectedTags.slice(0, this.maxDisplayedChips);
        },

        get hiddenTagCount() {
            return Math.max(0, this.selectedTags.length - this.maxDisplayedChips);
        },

        get categoryList() {
            // Return ordered list of categories for display
            // Spread copies the enumerable properties including the 'name' getter's current value
            const ids = [0, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
            return ids.map(id => ({ id, ...TAG_CATEGORIES[id] }));
        },

        get activeCategoryInfo() {
            if (this.activeCategory === null) return null;
            return this.categoryList.find(c => c.id === this.activeCategory);
        },

        get filteredResults() {
            // Filter out already selected tags from search results
            const selectedIds = new Set(this.selectedTags.map(t => String(t.id)));
            return this.searchResults.filter(tag => !selectedIds.has(String(tag.id)));
        },

        // Methods
        init() {
            // Handle ESC key to close modal
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.isOpen) {
                    this.close();
                }
            });
        },

        /**
         * Open the modal with initial tags
         * @param {Array} initialTags - Array of tag objects {id, name, cat_id}
         * @param {Function} onSave - Callback when save completes
         * @param {boolean} callbackOnly - If true, only call callback without saving to API
         */
        open(initialTags = [], onSave = null, callbackOnly = false) {
            this.selectedTags = [...initialTags];
            this.onSaveCallback = onSave;
            this.callbackOnly = callbackOnly;
            this.searchText = '';
            this.searchResults = [];
            this.activeCategory = null;
            this.offset = 0;
            this.hasMore = true;
            this.error = null;
            this.isOpen = true;

            // Prevent body scroll when modal is open (iOS-safe)
            lockBodyScroll();

            // Focus search input and refresh icons
            this.$nextTick(() => {
                const searchInput = this.$refs?.searchInput;
                if (searchInput) searchInput.focus();
                // Refresh Lucide icons for dynamically rendered content
                if (window.LucideIcons) {
                    window.LucideIcons.initialize(this.$el);
                }
            });
        },

        /**
         * Close the modal
         */
        close() {
            this.isOpen = false;
            unlockBodyScroll();

            // Clear state
            this.searchText = '';
            this.searchResults = [];
            this.activeCategory = null;
            this.onSaveCallback = null;
        },

        /**
         * Select a category to browse
         * @param {number} catId - Category ID (0 for popular)
         */
        selectCategory(catId) {
            this.activeCategory = catId;
            this.searchText = '';
            this.searchResults = [];
            this.offset = 0;
            this.hasMore = true;
            this.loadTags();
        },

        /**
         * Go back to category list
         */
        backToCategories() {
            this.activeCategory = null;
            this.searchText = '';
            this.searchResults = [];
            this.offset = 0;
            this.refreshIcons();
        },

        /**
         * Handle search input with debounce
         */
        onSearchInput() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.offset = 0;
                this.searchResults = [];
                this.hasMore = true;
                this.loadTags();
            }, SEARCH_DEBOUNCE_MS);
        },

        /**
         * Load tags from API
         */
        async loadTags() {
            if (this.isLoading || !this.hasMore) return;

            this.isLoading = true;
            this.error = null;

            try {
                const token = await window.getApiToken();
                if (!token) {
                    throw new Error('Authentication required');
                }

                // Handle "Most Popular" category (ID 0) - use curated list
                if (this.activeCategory === 0 && !this.searchText.trim()) {
                    const newTags = await this.loadPopularTags(token);
                    this.searchResults = newTags;
                    this.hasMore = false;
                    return;
                }

                const params = new URLSearchParams({
                    limit: PAGE_SIZE,
                    offset: this.offset
                });

                // Add category filter
                if (this.activeCategory !== null && this.activeCategory !== 0) {
                    params.set('cat_id', this.activeCategory);
                }

                // Add search text
                if (this.searchText.trim()) {
                    params.set('text', this.searchText.trim());
                }

                const response = await fetch(`/v1/search/tag?${params}`, {
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Accept': 'application/json'
                    },
                    credentials: 'include'
                });

                if (!response.ok) {
                    throw new Error('Failed to load interests');
                }

                const data = await response.json();
                const newTags = data.data || [];

                if (this.offset === 0) {
                    this.searchResults = newTags;
                } else {
                    this.searchResults = [...this.searchResults, ...newTags];
                }

                this.hasMore = newTags.length === PAGE_SIZE;
                this.offset += newTags.length;

            } catch (err) {
                console.error('Error loading tags:', err);
                this.error = err.message || 'Failed to load interests';
            } finally {
                this.isLoading = false;
                this.refreshIcons();
            }
        },

        /**
         * Load curated popular tags by their IDs
         * @param {string} token - API token
         * @returns {Promise<Array>} Array of tag objects
         */
        async loadPopularTags(token) {
            // Fetch tags by ID using the tag endpoint
            const tagIds = POPULAR_TAG_IDS.join(',');
            const response = await fetch(`/v1/tag/${tagIds}`, {
                headers: {
                    'X-AUTH-TOKEN': token,
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to load popular interests');
            }

            const data = await response.json();
            // The API returns an array directly when fetching multiple tags
            return Array.isArray(data) ? data : (data.data || []);
        },

        /**
         * Load more tags (infinite scroll)
         */
        loadMore() {
            if (!this.isLoading && this.hasMore) {
                this.loadTags();
            }
        },

        /**
         * Refresh Lucide icons after DOM updates
         */
        refreshIcons() {
            // Use setTimeout to ensure Alpine has fully rendered the DOM
            // This is necessary because Alpine's x-for template rendering
            // happens asynchronously and $nextTick alone isn't sufficient
            // Use document.body to ensure all dynamically added icons are processed
            setTimeout(() => {
                if (window.LucideIcons) {
                    window.LucideIcons.initialize(document.body);
                }
            }, 50);
        },

        /**
         * Toggle tag selection
         * @param {Object} tag - Tag object {id, name, cat_id}
         */
        toggleTag(tag) {
            const index = this.selectedTags.findIndex(t => String(t.id) === String(tag.id));

            if (index > -1) {
                // Remove tag
                this.selectedTags.splice(index, 1);
            } else if (this.canAddMore) {
                // Add tag
                this.selectedTags.push({
                    id: String(tag.id),
                    name: tag.name,
                    cat_id: parseInt(tag.cat_id, 10)
                });
            }
            this.refreshIcons();
        },

        /**
         * Remove a tag from selection
         * @param {string} tagId - Tag ID to remove
         */
        removeTag(tagId) {
            const index = this.selectedTags.findIndex(t => String(t.id) === String(tagId));
            if (index > -1) {
                this.selectedTags.splice(index, 1);
            }
            this.refreshIcons();
        },

        /**
         * Check if a tag is selected
         * @param {string} tagId - Tag ID
         * @returns {boolean}
         */
        isSelected(tagId) {
            return this.selectedTags.some(t => String(t.id) === String(tagId));
        },

        /**
         * Get category info for a tag
         * @param {number|string} catId - Category ID
         * @returns {Object}
         */
        getCategoryInfo(catId) {
            return getCategoryInfo(catId);
        },

        /**
         * Save selected tags
         */
        async save() {
            if (this.isSaving) return;

            this.isSaving = true;
            this.error = null;

            try {
                // If callbackOnly mode, just call the callback without saving to API
                if (this.callbackOnly) {
                    if (this.onSaveCallback) {
                        this.onSaveCallback(this.selectedTags);
                    }
                    this.isSaving = false;
                    this.close();
                    return;
                }

                const token = await window.getApiToken();
                if (!token) {
                    throw new Error('Authentication required');
                }

                const tagIds = this.selectedTags.map(t => t.id).join(',');

                const response = await fetch('/v1/user-tag/self', {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Accept': 'application/json'
                    },
                    credentials: 'include',
                    body: `tag_ids=${encodeURIComponent(tagIds)}`
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || 'Failed to save interests');
                }

                const data = await response.json();

                // Update selected tags with server response
                if (data.data) {
                    this.selectedTags = data.data.map(t => ({
                        id: String(t.id),
                        name: t.name,
                        cat_id: parseInt(t.cat_id, 10)
                    }));
                }

                // Call save callback
                if (this.onSaveCallback) {
                    this.onSaveCallback(this.selectedTags);
                }

                // Dispatch event for other components to listen
                window.dispatchEvent(new CustomEvent('interests-updated', {
                    detail: { tags: this.selectedTags }
                }));

                this.close();

            } catch (err) {
                console.error('Error saving interests:', err);
                this.error = err.message || 'Failed to save interests';
            } finally {
                this.isSaving = false;
            }
        },

        /**
         * Handle scroll for infinite loading
         */
        onScroll(event) {
            const el = event.target;
            const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

            if (scrollBottom < 100 && !this.isLoading && this.hasMore && this.activeCategory !== null) {
                this.loadMore();
            }
        }
    };
}

/**
 * Alpine.js component for interests display with edit capability
 */
function interestsDisplay() {
    return {
        tags: [],
        isEditable: false,
        isRemoving: false,

        init() {
            // Parse initial tags from data attribute
            try {
                const tagsData = this.$el.dataset.tags;
                if (tagsData) {
                    this.tags = JSON.parse(tagsData);
                }
            } catch (e) {
                console.error('Failed to parse tags data:', e);
            }

            this.isEditable = this.$el.dataset.editable === 'true';

            // Listen for updates
            window.addEventListener('interests-updated', (e) => {
                if (e.detail?.tags) {
                    this.tags = e.detail.tags;
                    this.refreshIcons();
                }
            });

            // Refresh icons after init
            this.$nextTick(() => this.refreshIcons());
        },

        refreshIcons() {
            this.$nextTick(() => {
                if (window.LucideIcons) {
                    window.LucideIcons.initialize(this.$el);
                }
            });
        },

        getCategoryInfo(catId) {
            return getCategoryInfo(catId);
        },

        /**
         * Remove a tag directly from profile (saves to API)
         */
        async removeTag(tagId) {
            if (this.isRemoving) return;

            // Optimistically remove from UI
            const removedIndex = this.tags.findIndex(t => String(t.id) === String(tagId));
            if (removedIndex === -1) return;

            const removedTag = this.tags[removedIndex];
            this.tags.splice(removedIndex, 1);
            this.isRemoving = true;

            try {
                const token = await window.getApiToken();
                if (!token) throw new Error('Authentication required');

                const tagIds = this.tags.map(t => t.id).join(',');
                const response = await fetch('/v1/user-tag/self', {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Accept': 'application/json'
                    },
                    credentials: 'include',
                    body: `tag_ids=${encodeURIComponent(tagIds)}`
                });

                if (!response.ok) {
                    throw new Error('Failed to remove interest');
                }

                // Dispatch event for other components
                window.dispatchEvent(new CustomEvent('interests-updated', {
                    detail: { tags: this.tags }
                }));

            } catch (err) {
                console.error('Error removing tag:', err);
                // Rollback on error
                this.tags.splice(removedIndex, 0, removedTag);
            } finally {
                this.isRemoving = false;
                this.refreshIcons();
            }
        },

        openEditModal() {
            // Dispatch event to open the global interests modal
            window.dispatchEvent(new CustomEvent('open-interests-modal', {
                detail: {
                    tags: this.tags,
                    onSave: (newTags) => {
                        this.tags = newTags;
                        this.refreshIcons();
                    }
                }
            }));
        }
    };
}

/**
 * Alpine.js component for search filter interests selection
 * Used in the search page filter dropdown
 */
function searchInterestsFilter() {
    return {
        selectedTags: [],
        suggestions: [],

        get filteredSuggestions() {
            const selectedIds = new Set(this.selectedTags.map(t => String(t.id)));
            return this.suggestions.filter(s => !selectedIds.has(String(s.id))).slice(0, 8);
        },

        addTag(tag) {
            if (!this.selectedTags.some(t => String(t.id) === String(tag.id))) {
                this.selectedTags.push({ id: String(tag.id), name: tag.name, cat_id: parseInt(tag.cat_id, 10) });
            }
        },

        init() {
            // Parse initial tags from form hidden inputs
            const form = this.$el.closest('form');
            if (form) {
                const tagInputs = form.querySelectorAll('input[name="tags[]"]');
                const tagIds = Array.from(tagInputs).map(input => input.value);

                // If we have initial tags, we need to load their details
                if (tagIds.length > 0) {
                    this.loadTagDetails(tagIds);
                }
            }

            // Listen for updates from the modal
            window.addEventListener('interests-updated', (e) => {
                if (e.detail?.tags) {
                    this.selectedTags = e.detail.tags;
                }
            });

            // Also listen for modal-specific updates for search filter
            window.addEventListener('search-interests-updated', (e) => {
                if (e.detail?.tags) {
                    this.selectedTags = e.detail.tags;
                }
            });
        },

        async loadTagDetails(tagIds) {
            if (!tagIds.length) return;

            try {
                const token = await window.getApiToken();
                if (!token) return;

                const response = await fetch(`/v1/tag/${tagIds.join(',')}`, {
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Accept': 'application/json'
                    },
                    credentials: 'include'
                });

                if (response.ok) {
                    const data = await response.json();
                    // Handle both single tag and array response
                    const tags = data.data || (Array.isArray(data) ? data : [data]);
                    this.selectedTags = tags.map(t => ({
                        id: String(t.id),
                        name: t.name,
                        cat_id: parseInt(t.cat_id, 10)
                    }));
                }
            } catch (err) {
                console.error('Failed to load tag details:', err);
            }
        },

        getCategoryInfo(catId) {
            return getCategoryInfo(catId);
        },

        removeTag(tagId) {
            const index = this.selectedTags.findIndex(t => String(t.id) === String(tagId));
            if (index > -1) {
                this.selectedTags.splice(index, 1);
            }
        },

        openModal() {
            // Dispatch event to open the global interests modal
            // Use callbackOnly mode so it doesn't save to user profile
            window.dispatchEvent(new CustomEvent('open-interests-modal', {
                detail: {
                    tags: this.selectedTags,
                    callbackOnly: true,
                    onSave: (newTags) => {
                        this.selectedTags = newTags;
                        // Dispatch a search-specific event
                        window.dispatchEvent(new CustomEvent('search-interests-updated', {
                            detail: { tags: newTags }
                        }));
                    }
                }
            }));
        }
    };
}

// Export globally for backward compatibility (used by some inline scripts)
window.TAG_CATEGORIES = TAG_CATEGORIES;
window.getCategoryInfo = getCategoryInfo;
